import { Component } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip } from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";
import { useMaterialUIController } from "context";
import { formatLocalDateTime } from "utils/dateFormatter";
import AddTemplateDialog from "./components/AddTemplateDialog/AddTemplateDialog";

interface TemplateListState {
    templates: any[];
    loading: boolean;
    searchTerm: string;
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    addTemplateDialogOpen: boolean;
    editingTemplate: any | null;
    isDeleteDialogOpen: boolean;
    deletingTemplate: any | null;
    deleteError: string | null;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_template_list_page_size";

class TemplateListScreenClass extends Component<any, TemplateListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 20;

        this.state = {
            templates: [],
            loading: false,
            searchTerm: "",
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            addTemplateDialogOpen: false,
            editingTemplate: null,
            isDeleteDialogOpen: false,
            deletingTemplate: null,
            deleteError: null,
        };
    }

    async componentDidMount() {
        this.loadTemplates();
    }

    loadTemplates = async () => {
        try {
            this.setState({ loading: true });

            const SELECTED_TENANT_KEY = "mediinstep_selected_tenant";
            const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                Name: this.state.searchTerm || undefined,
                TenantSeqId: savedTenantId ? parseInt(savedTenantId, 10) : undefined,
            };

            const res = await request("NotificationTemplate.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                this.setState({
                    templates: pagedList.items || [],
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("알림 템플릿 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        this.setState({ searchTerm, pageNumber: 1 }, () => {
            this.loadTemplates();
        });
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadTemplates();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadTemplates();
        });
    };

    handleAddTemplateOpen = () => {
        this.setState({ addTemplateDialogOpen: true });
    };

    handleAddTemplateClose = () => {
        this.setState({ addTemplateDialogOpen: false, editingTemplate: null });
    };

    handleAddTemplateSuccess = () => {
        this.setState({ pageNumber: 1, searchTerm: "", editingTemplate: null }, () => {
            this.loadTemplates();
        });
    };

    handleEdit = (template: any) => {
        this.setState({
            editingTemplate: template,
            addTemplateDialogOpen: true,
        });
    };

    handleOpenDeleteDialog = (template: any) => {
        this.setState({ deletingTemplate: template, isDeleteDialogOpen: true });
    };

    handleCloseDeleteDialog = () => {
        this.setState({ isDeleteDialogOpen: false, deletingTemplate: null, deleteError: null });
    };

    handleConfirmDelete = async () => {
        if (!this.state.deletingTemplate) return;

        try {
            this.setState({ loading: true, deleteError: null });
            await request('NotificationTemplate.delete', {
                pathParams: { id: this.state.deletingTemplate.seqId }
            });
            this.setState({ loading: false, isDeleteDialogOpen: false, deletingTemplate: null });
            this.loadTemplates();
        } catch (error: any) {
            console.error('알림 템플릿 삭제 실패:', error);
            this.setState({ loading: false, deleteError: error?.response?.data?.message || '알림 템플릿 삭제 중 오류가 발생했습니다.' });
        }
    };

    render() {
        const { templates, loading, searchTerm, pageSize, pageNumber, totalCount } = this.state;

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    {/* 검색 영역 */}
                    <Card>
                        <MDBox p={3}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">notifications</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        템플릿 목록 ( NOTIFICATION TEMPLATE LIST )
                                    </MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" onClick={this.handleAddTemplateOpen}>
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;템플릿 생성
                                </MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Autocomplete
                                        disableClearable
                                        value={this.state.pageSize.toString()}
                                        options={["5", "10", "15", "20", "25", "30", "40", "50"]}
                                        renderInput={(params) => (
                                            <MDInput {...params} />
                                        )}
                                        size="small"
                                        sx={{ width: "5rem" }}
                                        onChange={(_, newValue) => {
                                            if (newValue) {
                                                this.handlePageSizeChange(Number(newValue));
                                            }
                                        }}
                                    />
                                    <MDTypography variant="caption" color="secondary">
                                        &nbsp;&nbsp;entries per page
                                    </MDTypography>
                                </MDBox>

                                <MDBox width="12rem" ml="auto">
                                    <MDInput
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={this.handleSearch}
                                        size="small"
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Icon fontSize="small" sx={{ color: '#9e9e9e' }}>
                                                        search
                                                    </Icon>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    </Card>

                    {/* 리스트 영역 */}
                    <MDBox mt={3}>
                        {loading ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                            </MDBox>
                        ) : templates.length === 0 ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDTypography variant="h6" color="text">
                                    {searchTerm ? "검색 결과가 없습니다." : "등록된 템플릿이 없습니다."}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {templates.map((template: any) => {
                                        const gridProps = this.props.miniSidenav
                                            ? { xs: 12, sm: 6, md: 4, lg: 4, xl: 2.4 }
                                            : { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 };

                                        const sxProps = this.props.miniSidenav
                                            ? {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '16.666667%',
                                                    maxWidth: '16.666667%'
                                                }
                                            }
                                            : {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '20%',
                                                    maxWidth: '20%'
                                                }
                                            };

                                        return (
                                            <Grid item {...gridProps} sx={sxProps} key={template.seqId}>
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'all 0.2s ease-in-out',
                                                        cursor: 'pointer',
                                                        border: this.props.darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                                        boxShadow: this.props.darkMode
                                                            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                                                            : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: this.props.darkMode
                                                                ? '0 8px 20px rgba(255, 255, 255, 0.15)'
                                                                : '0 8px 20px rgba(0, 0, 0, 0.15)',
                                                            borderColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                                                        },
                                                    }}
                                                >
                                                    <MDBox p={3}>
                                                        {/* 헤더 */}
                                                        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                                            <MDBox display="flex" alignItems="center" gap={1} flex={1}>
                                                                <MDTypography variant="h5" fontWeight="bold">
                                                                    {template.name}
                                                                </MDTypography>
                                                                {template.isSystemTemplate && (
                                                                    <Chip
                                                                        label="시스템"
                                                                        size="small"
                                                                        sx={{
                                                                            backgroundColor: "#607d8b",
                                                                            color: "#ffffff",
                                                                            fontWeight: "bold",
                                                                            fontSize: "0.7rem",
                                                                            height: "20px",
                                                                            "& .MuiChip-label": {
                                                                                color: "#ffffff",
                                                                                px: 1,
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                            </MDBox>
                                                            <Chip
                                                                label={template.isActive ? "활성" : "비활성"}
                                                                color={template.isActive ? "success" : "default"}
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: "medium",
                                                                    ...(template.isActive && {
                                                                        backgroundColor: "#4CAF50",
                                                                        color: "#ffffff",
                                                                        "& .MuiChip-label": {
                                                                            color: "#ffffff",
                                                                        },
                                                                    }),
                                                                }}
                                                            />
                                                        </MDBox>

                                                        <Divider sx={{ my: 2 }} />

                                                        <MDBox>
                                                            {/* 코드 */}
                                                            <MDBox display="flex" alignItems="center" mb={1.5}>
                                                                <Icon fontSize="small" sx={{ mr: 1.5, color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                                                    code
                                                                </Icon>
                                                                <MDTypography variant="body2" color="text">
                                                                    {template.code}
                                                                </MDTypography>
                                                            </MDBox>

                                                            {/* 타입 */}
                                                            <MDBox display="flex" alignItems="center" mb={1.5}>
                                                                <Icon fontSize="small" sx={{ mr: 1.5, color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                                                    category
                                                                </Icon>
                                                                <Chip
                                                                    label={template.type}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="info"
                                                                />
                                                            </MDBox>

                                                            {/* 제목 템플릿 */}
                                                            <MDBox display="flex" alignItems="flex-start" mb={1.5}>
                                                                <Icon fontSize="small" sx={{ mr: 1.5, mt: 0.3, color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                                                    title
                                                                </Icon>
                                                                <MDTypography
                                                                    variant="body2"
                                                                    color="text"
                                                                    sx={{
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: 'vertical',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        flex: 1,
                                                                    }}
                                                                >
                                                                    {template.titleTemplate}
                                                                </MDTypography>
                                                            </MDBox>

                                                            {/* 본문 템플릿 */}
                                                            <MDBox display="flex" alignItems="flex-start" mb={1.5}>
                                                                <Icon fontSize="small" sx={{ mr: 1.5, mt: 0.3, color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                                                    description
                                                                </Icon>
                                                                <MDTypography
                                                                    variant="body2"
                                                                    color="text"
                                                                    sx={{
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 3,
                                                                        WebkitBoxOrient: 'vertical',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        flex: 1,
                                                                    }}
                                                                >
                                                                    {template.bodyTemplate}
                                                                </MDTypography>
                                                            </MDBox>

                                                            {/* 생성일 및 액션 버튼 */}
                                                            <MDBox display="flex" alignItems="center" justifyContent="space-between">
                                                                <MDBox display="flex" alignItems="center">
                                                                    <Icon fontSize="small" sx={{ mr: 1.5, color: this.props.darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)' }}>
                                                                        schedule
                                                                    </Icon>
                                                                    <MDTypography variant="caption" color="text.secondary">
                                                                        등록일: {formatLocalDateTime(template.crtDt)}
                                                                    </MDTypography>
                                                                </MDBox>
                                                                <MDBox display="flex" gap={0.5}>
                                                                    {/* Edit button - always visible */}
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            this.handleEdit(template);
                                                                        }}
                                                                        sx={{
                                                                            color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                                                            '&:hover': {
                                                                                backgroundColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                                                                color: '#49a3f1',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <Icon fontSize="small">edit</Icon>
                                                                    </IconButton>
                                                                    {/* Delete button - hidden for system templates */}
                                                                    {!template.isSystemTemplate && (
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                this.handleOpenDeleteDialog(template);
                                                                            }}
                                                                            sx={{
                                                                                color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                                                                '&:hover': {
                                                                                    backgroundColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                                                                    color: '#f44336',
                                                                                },
                                                                            }}
                                                                        >
                                                                            <Icon fontSize="small">delete</Icon>
                                                                        </IconButton>
                                                                    )}
                                                                </MDBox>
                                                            </MDBox>
                                                        </MDBox>
                                                    </MDBox>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {/* 페이지네이션 */}
                                {this.state.totalCount > 0 && (
                                    <MDBox mt={4} display="flex" justifyContent="center">
                                        <Pagination
                                            count={Math.ceil(this.state.totalCount / this.state.pageSize)}
                                            page={this.state.pageNumber}
                                            onChange={(_, page) => this.handlePageChange(page - 1)}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                                },
                                                '& .Mui-selected': {
                                                    backgroundColor: '#49a3f1 !important',
                                                    color: '#ffffff !important',
                                                },
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </>
                        )}
                    </MDBox>
                </MDBox>
                <Footer />

                {/* Add/Edit Dialog */}
                <AddTemplateDialog
                    open={this.state.addTemplateDialogOpen}
                    editingTemplate={this.state.editingTemplate}
                    onClose={this.handleAddTemplateClose}
                    onSuccess={this.handleAddTemplateSuccess}
                    theme={this.props.theme}
                    darkMode={this.props.darkMode}
                />

                {/* 삭제 확인 다이얼로그 */}
                <Dialog
                    open={this.state.isDeleteDialogOpen}
                    onClose={this.handleCloseDeleteDialog}
                    PaperProps={{
                        sx: {
                            backgroundColor: this.props.darkMode ? '#1a2035' : '#fff',
                            backgroundImage: 'none',
                        }
                    }}
                >
                    <DialogTitle sx={{ color: this.props.darkMode ? '#fff' : '#000' }}>
                        알림 템플릿 삭제 확인
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText sx={{ color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                            "{this.state.deletingTemplate?.name}" 템플릿을 삭제하시겠습니까?<br />
                            이 작업은 취소할 수 없습니다.
                        </DialogContentText>
                        {this.state.deleteError && (
                            <MDBox mt={2}>
                                <p style={{ color: '#f44336', fontSize: '0.875rem', margin: 0 }}>
                                    {this.state.deleteError}
                                </p>
                            </MDBox>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={this.handleCloseDeleteDialog} color="secondary">
                            취소
                        </MDButton>
                        <MDButton
                            onClick={this.handleConfirmDelete}
                            variant="gradient"
                            color="error"
                            disabled={loading}
                        >
                            {loading ? '삭제 중...' : '삭제'}
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </DashboardLayout>
        );
    }
}

function TemplateListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode, miniSidenav } = controller;

    return <TemplateListScreenClass {...props} darkMode={darkMode} miniSidenav={miniSidenav} />;
}

export default TemplateListScreen;
